import { useUiLanguage } from "@/i18n/useUiLanguage";
import { tr, localizedStatic } from "@/i18n/copy";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AgentModuleSection from "@/components/AgentModuleSection";
import {
  BarChart3, Bot, FileText, HardDrive, Landmark, Megaphone, QrCode,
  Settings2, ShieldCheck, Sparkles, UsersRound, WalletCards
} from "lucide-react";

const MODULES = localizedStatic([
  { id:"loyalty", title:"Fidelizzazione & Marketing", description:"Bonus, Gift Card, recensioni e campagne PWA.", route:"/growth", icon:Megaphone, module:"loyalty" },
  { id:"finance", title:"Finanza & Checkout", description:"Incassi, rendiconti Team e storico fatture.", route:"/finance", icon:Landmark, module:"inventory" },
  { id:"warehouse", title:"Magazzino", description:"Prodotti, lotti, scadenze, fornitori e materiali dei professionisti.", route:"/warehouse", icon:HardDrive, module:"inventory" },
  { id:"analytics", title:"Report & Analytics", description:"Andamento operativo, economico e commerciale dello Studio.", route:"/reports", icon:BarChart3, module:"analytics" },
  { id:"scanner", title:"QR ID & Check-in", description:"Identità Cliente, conformità e check-in operativo. Le azioni economiche rispettano i permessi del ruolo.", route:"/scanner", icon:QrCode, module:"scheduling" },
  { id:"documents", title:"Documenti & Backup", description:"Archivio Studio, fascicoli Team e collegamento cloud.", route:"/documents", icon:HardDrive, module:"documents" },
  { id:"team", title:"Team, ruoli e postazioni", description:"Persone, ruoli, cabine, postazioni e accordi economici.", route:"/team", icon:UsersRound },
  { id:"settings", title:"Configurazione Studio", description:"Identità, orari, fiscalità, pagamenti e comunicazioni.", route:"/settings", icon:Settings2 },
  { id:"billing", title:"Piano & moduli", description:"Piano attivo e moduli commerciali disponibili.", route:"/billing", icon:WalletCards },
], [[0,"title"],[0,"description"],[1,"title"],[1,"description"],[2,"title"],[2,"description"],[3,"title"],[3,"description"],[4,"title"],[4,"description"],[5,"title"],[5,"description"],[6,"title"],[6,"description"],[7,"title"],[7,"description"],[8,"title"],[8,"description"]]);

const FUTURE = localizedStatic([
  { title:"TPS AI Assistant", description:"Predisposizione per un assistente AI operativo con permessi controllati, azioni e audit.", icon:Bot },
  { title:"Automazioni avanzate", description:"Trigger e azioni configurabili su lifecycle, CRM, follow-up e operatività.", icon:Sparkles },
  { title:"Integrazioni fiscali/provider", description:"Provider esterni per pagamenti, fatturazione e servizi professionali.", icon:ShieldCheck },
], [[0,"title"],[0,"description"],[1,"title"],[1,"description"],[2,"title"],[2,"description"]]);

export default function FeatureCenter() {
  const uiLanguage = useUiLanguage();
  const nav = useNavigate();
  const { user, hasModule, entitlements } = useAuth();
  const cards = useMemo(() => MODULES.map((item) => ({
    ...item,
    active: !item.module || hasModule(item.module),
  })), [hasModule, uiLanguage]);

  return <div className="animate-fade-up space-y-6">
    <PageHeader
      title={tr("Moduli & Integrazioni")}
      subtitle={tr("Un solo punto di controllo: ogni funzione rimanda al modulo canonico che possiede realmente i dati.")}
    />

    <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {cards.map(({ id,title,description,route,icon:Icon,active }) =>
        <Card key={id} className="flex min-h-48 flex-col p-5 bg-card/60">
          <div className="flex items-start justify-between gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10"><Icon className="h-5 w-5 text-primary"/></span>
            <Badge variant={active ? "default" : "outline"}>{active ? tr("Attivo") : tr("Non incluso")}</Badge>
          </div>
          <h2 className="mt-4 font-display text-lg font-semibold">{title}</h2>
          <p className="mt-1 flex-1 text-sm text-muted-foreground">{description}</p>
          <Button className="mt-4" variant={active ? "outline" : "secondary"} onClick={()=>nav(active ? route : "/billing")}>
            {active ? tr("Apri modulo") : tr("Vedi piano")}
          </Button>
        </Card>
      )}
    </section>

    {user?.role === "studio_owner" && <AgentModuleSection />}

    <section className="rounded-2xl border bg-card/40 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="label-tiny text-primary">{tr("Architettura commerciale")}</p><h2 className="font-display text-xl font-semibold">{tr("Predisposizione pacchetti TPS")}</h2><p className="mt-1 max-w-3xl text-sm text-muted-foreground">{tr("La piattaforma è già strutturata per separare moduli e permessi dal piano principale. La composizione definitiva Base / Studio / Ultimate e i pacchetti annuali verrà chiusa nel collaudo Super Admin.")}</p></div>
        <Badge variant="outline">{entitlements?.plan_code || "trial"}</Badge>
      </div>
    </section>

    <section>
      <div className="mb-3"><p className="label-tiny text-primary">{tr("Predisposizioni future")}</p><h2 className="font-display text-xl font-semibold">{tr("Non ancora vendute come funzione attiva")}</h2></div>
      <div className="grid gap-3 md:grid-cols-3">
        {FUTURE.map(({title,description,icon:Icon})=><Card key={title} className="p-4 bg-card/40 border-dashed"><Icon className="h-5 w-5 text-muted-foreground"/><h3 className="mt-3 font-semibold">{title}</h3><p className="mt-1 text-sm text-muted-foreground">{description}</p><Badge variant="outline" className="mt-3">{tr("Predisposto")}</Badge></Card>)}
      </div>
    </section>

    <section className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground">
      <div className="flex gap-3"><FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary"/><p><b className="text-foreground">{tr("Regola TPS:")}</b> {tr("questo centro non crea clienti, bonus, pagamenti o campagne. I dati vengono creati soltanto nel loro modulo canonico, evitando doppioni e flussi concorrenti.")}</p></div>
    </section>
  </div>;
}
